/**
 * Where this app lives on its host: Vite's `base`, "/" for an app at the root of its host and "/admin/" for the Platform Console under
 * `ncp.moet.gov.ae/admin` (the Ministry's internal hosts put the back office, the console and the portal on one host). Every API call names
 * it in `X-NCP-App`, so the API binds each app's session and refresh cookie to the app rather than to the shared origin; hard navigations,
 * the sign-in page's return address and public files are under it too. On a host of its own (base "/") every helper here is the identity.
 */
export const APP_BASE: string = normaliseBase((import.meta as ImportMeta & { env?: { BASE_URL?: string } }).env?.BASE_URL);

/** The header that names the app on every API call (`SpaOrigins.AppHeader` in the API). */
export const APP_HEADER = "X-NCP-App";

function normaliseBase(base: string | undefined): string {
  if (!base || !base.startsWith("/")) return "/";
  return base.endsWith("/") ? base : `${base}/`;
}

/** A path of this app as the browser's path: `appPath("/login")` is "/admin/login" in the console under /admin. */
export function appPath(path: string): string {
  return `${APP_BASE}${path.replace(/^\//, "")}`;
}

/** The browser's path as a path of this app (the router's view): "/admin/users" is "/users" in the console under /admin. */
export function routerPath(pathname: string): string {
  if (APP_BASE === "/") return pathname;
  const root = APP_BASE.slice(0, -1);
  if (pathname === root) return "/";
  return pathname.startsWith(APP_BASE) ? pathname.slice(root.length) : pathname;
}

/** Whether a URL is on this host under this app's base path. An app at the root of its host cannot tell another app below it from its own pages. */
export function underAppBase(url: URL): boolean {
  return url.origin === window.location.origin && (url.pathname === APP_BASE.slice(0, -1) || url.pathname.startsWith(APP_BASE));
}
